import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";
import Avatar from "@/components/Avatar";
import { BRAND } from "@/lib/brand";
import { ArrowRight, BookHeart, Mic2, MoonStar, Phone } from "lucide-react";

export default function Landing() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();

    useEffect(() => {
        const ref = params.get("ref");
        if (ref) {
            try { localStorage.setItem("pax_ref_code", ref); } catch (e) { void e; }
        }
    }, [params]);

    useEffect(() => {
        if (user) navigate("/companion", { replace: true });
    }, [user, navigate]);

    return (
        <div className="relative z-10 min-h-screen">
            <header className="px-6 md:px-12 py-6 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2.5" data-testid="landing-brand">
                    <img src={BRAND.logo} alt={BRAND.fullName} className="w-9 h-9 object-contain" data-testid="brand-logo" />
                    <span className="font-heading text-2xl tracking-tight text-ink">{BRAND.handle}</span>
                </Link>
                <div className="flex items-center gap-5">
                    <Link to="/signin" data-testid="nav-signin" className="text-sm text-inkSoft hover:text-ink font-body">sign in</Link>
                    <Link
                        to="/signup"
                        data-testid="nav-signup"
                        className="text-sm bg-ink text-bone px-5 py-2.5 rounded-full hover:bg-terracotta transition-colors font-body"
                    >
                        begin
                    </Link>
                </div>
            </header>

            {/* Hero */}
            <section className="px-6 md:px-12 pt-8 md:pt-16 grid md:grid-cols-2 gap-12 md:gap-16 items-center">
                <div>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                    >
                        <p data-testid="hero-tagline" className="text-sm uppercase tracking-[0.3em] text-inkSoft mb-6 font-body">
                            {BRAND.tagline}
                        </p>
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05] mb-6">
                            someone to <span className="italic text-terracotta">sit with you</span>,
                            <br />when the day gets heavy.
                        </h1>
                        <p className="text-lg text-inkSoft font-body leading-relaxed max-w-md mb-10">
                            {BRAND.description}
                        </p>
                        <div className="flex flex-wrap items-center gap-4">
                            <Link
                                to="/signup"
                                data-testid="hero-cta-begin"
                                className="group inline-flex items-center gap-2 bg-terracotta text-bone px-7 py-4 rounded-full hover:bg-terracottaHover transition-all hover:scale-[1.02] font-body"
                            >
                                begin a conversation
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <Link to="/signin" className="text-ink underline-offset-4 hover:underline font-body">
                                I've been here before
                            </Link>
                        </div>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.92 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.0, delay: 0.2 }}
                    className="flex items-center justify-center"
                >
                    <Avatar state="idle" size={420} testId="hero-avatar" />
                </motion.div>
            </section>

            {/* App preview video */}
            <section className="px-6 md:px-12 py-16 md:py-24">
                <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 md:gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7 }}
                        className="flex justify-center order-2 md:order-1"
                    >
                        <div className="relative w-[240px] sm:w-[280px] rounded-[2.6rem] border-[10px] border-ink bg-ink shadow-clay overflow-hidden">
                            <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-16 h-1.5 rounded-full bg-ink z-10" />
                            <video
                                src="/pax-preview.mp4"
                                autoPlay
                                muted
                                loop
                                playsInline
                                className="w-full h-auto block rounded-[1.9rem]"
                                data-testid="landing-preview-video"
                            />
                        </div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="order-1 md:order-2"
                    >
                        <p className="text-sm uppercase tracking-[0.3em] text-inkSoft mb-5 font-body">20 seconds of pax</p>
                        <h2 className="font-heading text-3xl md:text-4xl tracking-tight text-ink leading-tight mb-5">
                            see what a conversation<br />actually feels like.
                        </h2>
                        <p className="text-inkSoft font-body leading-relaxed max-w-md mb-8">
                            a face that reacts, a voice that answers, a journal that remembers — and a week that slowly starts to make sense.
                        </p>
                        <Link
                            to="/signup"
                            data-testid="video-cta"
                            className="inline-flex items-center gap-2 text-terracotta font-body hover:underline underline-offset-4"
                        >
                            try it yourself <ArrowRight className="w-4 h-4" />
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* Features bento */}
            <section className="px-6 md:px-12 py-24 mt-12">
                <div className="grid md:grid-cols-3 gap-5 max-w-6xl mx-auto">
                    <Feature
                        icon={<Mic2 className="w-6 h-6 text-terracotta" />}
                        title="speak, don't type"
                        body="press the circle. say what you'd say to a friend. pax listens, then speaks back in a warm voice."
                    />
                    <Feature
                        icon={<BookHeart className="w-6 h-6 text-sage" />}
                        title="a journal that holds you"
                        body="end the day with a few sentences. pax will remember the threads, gently."
                    />
                    <Feature
                        icon={<MoonStar className="w-6 h-6 text-amber" />}
                        title="quiet at 3am"
                        body="no waiting rooms. no judgement. always available — for the small things and the big ones."
                    />
                </div>
            </section>

            {/* Disclosure + helplines */}
            <section className="px-6 md:px-12 pb-16">
                <div className="max-w-2xl mx-auto bg-clay/60 border border-clayHover rounded-3xl p-8">
                    <p className="font-journal italic text-ink text-lg leading-relaxed text-center">
                        "{BRAND.name} is not a replacement for professional care. if you are in crisis, please reach out to a real human."
                    </p>
                    <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
                        <a
                            href="tel:18005990019"
                            data-testid="landing-helpline-india"
                            className="flex items-center gap-3 bg-white border border-clayHover text-ink rounded-2xl px-5 py-3 hover:bg-bone transition-colors font-body"
                        >
                            <Phone className="w-4 h-4 text-terracotta" />
                            <div>
                                <div className="font-medium">India · KIRAN</div>
                                <div className="text-xs text-inkSoft">1800-599-0019 · 24/7 · free</div>
                            </div>
                        </a>
                        <a
                            href="tel:988"
                            data-testid="landing-helpline-us"
                            className="flex items-center gap-3 bg-white border border-clayHover text-ink rounded-2xl px-5 py-3 hover:bg-bone transition-colors font-body"
                        >
                            <Phone className="w-4 h-4 text-terracotta" />
                            <div>
                                <div className="font-medium">US · 988</div>
                                <div className="text-xs text-inkSoft">suicide & crisis lifeline</div>
                            </div>
                        </a>
                    </div>
                </div>
            </section>

            <footer className="px-6 md:px-12 py-10 text-center text-sm text-inkSoft font-body">
                <div className="flex flex-col items-center gap-3 opacity-80">
                    <div className="flex items-center gap-2">
                        <img src={BRAND.logo} alt="" className="w-5 h-5 object-contain" />
                        <span>{BRAND.fullName} · made with care · 2026</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                        <Link to="/privacy" data-testid="footer-privacy" className="hover:text-ink">privacy</Link>
                        <span className="opacity-40">·</span>
                        <Link to="/terms" data-testid="footer-terms" className="hover:text-ink">terms</Link>
                        <span className="opacity-40">·</span>
                        <Link to="/board" data-testid="footer-board" className="hover:text-ink">what we're fixing</Link>
                        <span className="opacity-40">·</span>
                        <a href="mailto:hello@paxmind.ai" className="hover:text-ink">contact</a>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function Feature({ icon, title, body }) {
    return (
        <div className="bg-white/60 border border-clayHover rounded-3xl p-8 hover:bg-white transition-colors shadow-soft">
            <div className="w-12 h-12 rounded-2xl bg-clay flex items-center justify-center mb-5">{icon}</div>
            <h3 className="font-heading text-xl tracking-tight text-ink mb-2">{title}</h3>
            <p className="text-inkSoft font-body leading-relaxed">{body}</p>
        </div>
    );
}
